// aiPanel.js — what the composer's focus panel OFFERS for the chosen context
// (DESIGN-COMPOSER-FOCUS-POPOVER-CONTEXT-INSPECT-1; the panel itself is the
// shared SimoneSuggestions, the context model is functions/aiScope.js).
//
//   aiPanelSuggestions(root)  the grouped rows: SUGGESTED (the scoped app's
//                             own starters, the page's intent, the built-in
//                             page / record sets, "What can you do here?")
//                             and RECENT (the member's own last asks) — at
//                             most _AIP_MAX rows
//   aiPanelActions(root)      the three first-class verbs: Inspect & ask,
//                             Summarize, Build a board
//   aiPanelRun(kind)          runs one verb for the chosen context
//   aiPanelSend(row)          sends one suggestion row
//
// The SEND is the composer's own (homeAiSend), so a panel ask lands where a
// typed one lands (the main thread, the sidebar, a board). The person's mode
// is respected for a suggestion; the two verbs that name their output carry
// it for that one ask — Summarize answers, Build a board builds — and never
// write the mode (functions/assistantMode.js: an explicit choice wins).

import { shellSay } from './shellSharedDelegates.js'
import {
  aiRecordSuggestionItems,
  aiPageSuggestionItems,
  aiGenericSuggestionItems
} from './pageContext.js'
import { aiScopeSurface, aiScopeCurrent, aiInstalledApps } from './aiScope.js'

const _AIP_MAX = 6
const _AIP_STARTERS_MAX = 3
const _AIP_RECENT_MAX = 2

const _aipStr = (v) => (v == null ? '' : String(v))
const _aipText = (el, key) => {
  const t = _aipStr(key)
  if (!t) return ''
  try {
    return el && typeof el.call === 'function' ? _aipStr(el.call('polyglot', t)) || t : t
  } catch (_) {
    return t
  }
}
// A sentence with a {name} hole, filled by a FUNCTION (a `$&` in a tenant's
// name must never read as a replacement pattern).
const _aipFill = (el, key, name) => shellSay(el, key).replace('{name}', () => _aipStr(name))
const _aipGlyph = (el, key) => {
  try {
    if (el && typeof el.call === 'function') {
      const n = el.call('glyphNameFor', { glyph: key }, 'ln')
      if (typeof n === 'string' && n) return n
    }
  } catch (_) {
    /* no resolver on this host */
  }
  return 'ln' + key[0].toUpperCase() + key.slice(1)
}

// A manifest starter that names its app's OWN FORM (`{ text, form }`, the
// merged stash adds `app` — workspaceProjects.js _composeAiStash) → the form
// ref '<app id>.<form id>', else ''.
const _aipStarterForm = (it, fallbackAppId) => {
  if (!it || typeof it !== 'object' || typeof it.form !== 'string') return ''
  if (!/^[a-z0-9-]+$/.test(it.form)) return ''
  const app = _aipStr(it.app || fallbackAppId).trim()
  return app ? app + '.' + it.form : ''
}

// One app's starters: its manifest list, a starter that names a PAGE first
// when the scope is that page (`{ text, route }` — the page-scoped starter of
// the module docs), each resolved through polyglot (a bundle key the app
// ships reads in the session language, a literal passes through).
const _aipAppStarters = (el, app, route) => {
  const list = Array.isArray(app && app.chatStarters) ? app.chatStarters : []
  const rows = []
  for (const it of list) {
    const text = typeof it === 'string' ? it : it && typeof it.text === 'string' ? it.text : ''
    if (!text.trim()) continue
    const own = it && typeof it === 'object' && typeof it.route === 'string' ? it.route : ''
    // a starter naming the app's OWN FORM opens it (assistantStarterForm)
    const form = _aipStarterForm(it, app && app.id)
    rows.push({
      raw: text.trim(),
      route: own,
      localAction: form
        ? 'assistantStarterForm'
        : it && typeof it.localAction === 'string'
          ? it.localAction
          : undefined,
      form
    })
  }
  const onPage = route ? rows.filter((r) => r.route === route) : []
  const general = rows.filter((r) => !r.route)
  return [...onPage, ...general].map((r) => {
    const text = _aipText(el, r.raw)
    return {
      kind: 'starter',
      text,
      prompt: text,
      localAction: r.localAction,
      ...(r.form ? { form: r.form } : {}),
      glyph: _aipGlyph(el, 'assistant')
    }
  })
}

// The member's own recent asks: this thread's user lines (newest first), then
// the other threads' titles. Never a placeholder title.
const _aipRecent = (el, root) => {
  const out = []
  const seen = new Set()
  const push = (t) => {
    const text = _aipStr(t).replace(/\s+/g, ' ').trim()
    const k = text.toLowerCase()
    if (text.length < 3 || text.length > 140 || seen.has(k)) return
    seen.add(k)
    out.push(text)
  }
  const msgs = Array.isArray(root && root.chatMessages) ? root.chatMessages : []
  for (let i = msgs.length - 1; i >= 0 && out.length < 6; i--) {
    const m = msgs[i]
    if (m && m.role === 'user') push(m.text)
  }
  const newChat = _aipText(el, 'workspace.simone.newChat').toLowerCase()
  const sessions = root && root.sessions && typeof root.sessions === 'object' ? root.sessions : {}
  const list = Object.values(sessions)
    .filter((x) => x && x.title)
    .sort((a, b) => (b.ts || 0) - (a.ts || 0))
  for (const sess of list) {
    if (out.length >= 6) break
    if (_aipStr(sess.title).toLowerCase() === newChat) continue
    push(sess.title)
  }
  return out
}

// The built-in sets (pageContext.js) as rows.
const _aipBuiltinRows = (el, items, on) =>
  items.map((s) => ({
    kind: 'intent',
    text: _aipText(el, s.labelKey),
    prompt: _aipText(el, s.promptKey),
    on: on || '',
    glyph: _aipGlyph(el, 'assistant')
  }))

// THE GROUPED ROWS for the chosen context — at most _AIP_MAX, de-duplicated by
// text, every row { key, kind, text, prompt, glyph, localAction?, generic? }.
export const aiPanelSuggestions = function aiPanelSuggestions(root) {
  const el = this
  const r = root && typeof root === 'object' ? root : {}
  const row = aiScopeCurrent.call(el, r)
  if (!row) return []
  const sf = aiScopeSurface.call(el, r)
  const apps = aiInstalledApps(r)
  const suggested = []
  const seen = new Set()
  const add = (list, row2) => {
    const k = _aipStr(row2.text).trim().toLowerCase()
    if (!k || seen.has(k)) return
    seen.add(k)
    list.push(row2)
  }

  // 1. THE CONTEXT'S OWN ASKS.
  if (row.kind === 'record' && sf.record) {
    for (const x of _aipBuiltinRows(el, aiRecordSuggestionItems(sf.record.type), sf.record.title)) {
      add(suggested, x)
    }
  } else if (row.kind === 'workspace') {
    // The workspace's own: one starter per app, in the round-robin order the
    // composed stash already keeps (workspaceProjects._composeAiStash).
    const merged = Array.isArray(r.workspaceProject && r.workspaceProject.chatStarters)
      ? r.workspaceProject.chatStarters
      : []
    for (const it of merged.slice(0, _AIP_STARTERS_MAX)) {
      const raw = typeof it === 'string' ? it : it && it.text
      const text = _aipText(el, raw)
      const form = _aipStarterForm(it, r.workspaceProject && r.workspaceProject.id)
      if (text) {
        add(suggested, {
          kind: 'starter',
          text,
          prompt: text,
          localAction: form
            ? 'assistantStarterForm'
            : it && typeof it === 'object'
              ? it.localAction
              : undefined,
          ...(form ? { form } : {}),
          glyph: _aipGlyph(el, 'assistant')
        })
      }
    }
  } else {
    const app = row.appId ? apps.find((a) => a.id === row.appId) : null
    const starters = app ? _aipAppStarters(el, app, row.kind === 'page' ? row.route : '') : []
    for (const x of starters.slice(0, _AIP_STARTERS_MAX)) add(suggested, x)
    // A built-in app's page set (Tasks, Calendar, Chat, Notes).
    if (!app && sf.app && row.key === 'app') {
      for (const x of _aipBuiltinRows(el, aiPageSuggestionItems(sf.path))) add(suggested, x)
    }
  }

  // 2. THE CONTEXT'S INTENT — what needs attention, said for this context.
  if (row.kind === 'page' || (row.kind === 'app' && !row.other && !sf.page)) {
    add(suggested, {
      kind: 'intent',
      text: shellSay(el, 'workspace.aiScope.intent.attention'),
      prompt: _aipFill(el, 'workspace.aiScope.intent.attentionPrompt', row.label),
      glyph: _aipGlyph(el, 'attention')
    })
  } else if (row.kind === 'app') {
    add(suggested, {
      kind: 'intent',
      text: _aipFill(el, 'workspace.aiScope.intent.attentionApp', row.label),
      prompt: _aipFill(el, 'workspace.aiScope.intent.attentionAppPrompt', row.label),
      glyph: _aipGlyph(el, 'attention')
    })
  } else if (row.kind === 'workspace') {
    const plate = aiGenericSuggestionItems()[0]
    if (plate) {
      add(suggested, {
        kind: 'intent',
        text: _aipText(el, plate.labelKey),
        prompt: _aipText(el, plate.promptKey),
        glyph: _aipGlyph(el, 'attention')
      })
    }
  }

  // 3. WHAT CAN YOU DO HERE — always offered, last, in the quieter ink: the
  // deterministic capabilities answer (functions/aiCapabilities.js).
  const help = {
    kind: 'capabilities',
    text: shellSay(el, 'workspace.aiSuggest.generic.whatCanYouDo'),
    prompt: shellSay(el, 'workspace.aiSuggest.generic.whatCanYouDo'),
    localAction: 'aiShowCapabilities',
    glyph: _aipGlyph(el, 'help'),
    generic: true
  }
  // the help row is a row above too: asking it once must not repeat it as a
  // recent ask (measured live on dev 2026-10-04)
  seen.add(_aipStr(help.text).trim().toLowerCase())

  // 4. RECENT — the member's own words, never a duplicate of a row above.
  const recent = []
  for (const text of _aipRecent(el, r)) {
    if (recent.length >= _AIP_RECENT_MAX) break
    const k = text.toLowerCase()
    if (seen.has(k)) continue
    recent.push({ kind: 'recent', text, prompt: text, glyph: _aipGlyph(el, 'history') })
  }

  // THE CAP: the context's rows give way to the member's own asks, never below
  // two of them; the help row always stays.
  const room = _AIP_MAX - 1 - recent.length
  const keep = Math.max(Math.min(2, suggested.length), Math.min(suggested.length, room))
  const shown = suggested.slice(0, keep)
  shown.push(help)
  const recentShown = recent.slice(0, Math.max(0, _AIP_MAX - shown.length))
  const stamp = (list, group) => list.map((x, i) => ({ ...x, key: group + '-' + i + '-' + x.kind }))
  const groups = [
    {
      key: 'suggested',
      label:
        row.kind === 'workspace'
          ? shellSay(el, 'workspace.aiScope.group.suggested')
          : _aipFill(el, 'workspace.aiScope.group.suggestedFor', row.short || row.label),
      rows: stamp(shown, 'suggested')
    }
  ]
  if (recentShown.length) {
    groups.push({
      key: 'recent',
      label: shellSay(el, 'workspace.aiScope.group.recent'),
      rows: stamp(recentShown, 'recent')
    })
  }
  return groups
}

// THE THREE VERBS — the same three in every context, each said for it.
export const aiPanelActions = function aiPanelActions(root) {
  const el = this
  const row = aiScopeCurrent.call(el, root)
  if (!row) return []
  return [
    {
      key: 'inspect',
      label: shellSay(el, 'workspace.aiScope.action.inspect'),
      hint: shellSay(el, 'workspace.aiScope.action.inspectHint'),
      glyph: _aipGlyph(el, 'inspect')
    },
    {
      key: 'summarize',
      label: shellSay(el, 'workspace.aiScope.action.summarize'),
      hint: _aipFill(el, 'workspace.aiScope.action.summarizeHint', row.label),
      glyph: _aipGlyph(el, 'summarize')
    },
    {
      key: 'board',
      label: shellSay(el, 'workspace.aiScope.action.board'),
      hint: _aipFill(el, 'workspace.aiScope.action.boardHint', row.label),
      glyph: _aipGlyph(el, 'dashboard')
    }
  ]
}

// The ask a verb sends for a context kind.
const _AIP_VERB_KEYS = {
  summarize: {
    record: 'workspace.aiScope.action.summarizePromptRecord',
    page: 'workspace.aiScope.action.summarizePromptPage',
    app: 'workspace.aiScope.action.summarizePromptApp',
    workspace: 'workspace.aiScope.action.summarizePromptWorkspace'
  },
  board: {
    record: 'workspace.aiScope.action.boardPromptRecord',
    page: 'workspace.aiScope.action.boardPromptPage',
    app: 'workspace.aiScope.action.boardPromptApp',
    workspace: 'workspace.aiScope.action.boardPromptWorkspace'
  }
}

// The words a verb sends for the chosen context (tests read it too).
export const aiPanelVerbPrompt = function aiPanelVerbPrompt(kind, root) {
  const el = this
  const row = aiScopeCurrent.call(el, root)
  const keys = _AIP_VERB_KEYS[kind]
  if (!row || !keys) return ''
  return _aipFill(el, keys[row.kind] || keys.app, row.label)
}

// The panel closes after a pick: the textarea keeps focus for the next words,
// the panel stays down until it is asked for again.
const _aipClosePanel = (el) => {
  const root = el && el.state && el.state.root
  if (root && typeof root.update === 'function') {
    root.update({ _aiPanel: 'dismissed' }, { preventFetch: true })
  }
}

// Run one verb for the chosen context.
export const aiPanelRun = function aiPanelRun(kind) {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function') return false
  if (kind === 'inspect') {
    _aipClosePanel(el)
    return el.call('aiInspectStart')
  }
  const text = aiPanelVerbPrompt.call(el, kind, root)
  if (!text) return false
  _aipClosePanel(el)
  el.call('homeAiSend', text, null, 0, null, {
    mode: kind === 'board' ? 'dashboard' : 'assistant'
  })
  return true
}

// Send one suggestion row: its own local action (a manifest starter's, the
// capabilities answer), else the composer's send in the person's mode.
export const aiPanelSend = function aiPanelSend(row) {
  const el = this
  const x = row && typeof row === 'object' ? row : null
  if (!x) return false
  _aipClosePanel(el)
  if (x.localAction) {
    try {
      // the row rides along (assistantStarterForm reads its `form`)
      el.call(x.localAction, x)
      return true
    } catch (_) {
      /* the action's host is gone — fall through to asking it */
    }
  }
  const text = _aipStr(x.prompt || x.text).trim()
  if (!text) return false
  el.call('homeAiSend', text)
  return true
}
